import { usageFunctionsSchema, type AssetDocumentDto, type UsageFunction } from "./dossier.js";
import type { ObligationStatus } from "./obligations.js";
import { z } from "zod";
import { ASSET_LINK_KINDS, FILE_KINDS, type AssetLinkKind } from "./enums.js";
import { isNlsfbCode } from "./nlsfb.js";
import { ASSET_KINDS, type AssetKind } from "./condition.js";
import { ASSET_TYPES, type AssetType } from "./assetTypes.js";
import { LIBRARY_UNITS, type LibraryUnit } from "./library.js";
import { buildingCodeSchema } from "./ids.js";
import { floorScaleSchema, type FloorScale } from "./planScale.js";

const trimmed = (max: number) => z.string().trim().max(max);
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v ? v : null));

/** Coördinaat genormaliseerd 0–1 t.o.v. de tekening. */
export const coordSchema = z.number().min(0).max(1);
export const pointSchema = z.tuple([coordSchema, coordSchema]);
/** Vlak van een ruimte; een rechthoek is een polygoon met 4 punten. */
export const roomShapeSchema = z.object({
  type: z.literal("poly"),
  points: z.array(pointSchema).min(3).max(500),
});
export type RoomShape = z.infer<typeof roomShapeSchema>;

const buildingFields = z.object({
  id: z.uuid().optional(),
  code: buildingCodeSchema,
  name: trimmed(120).min(1, "Vul een naam in"),
  /** Weergave van het adres; bij een gekozen BAG-adres samengesteld uit de delen. */
  address: optionalText(200),
  street: optionalText(120),
  houseNumber: optionalText(20),
  postcode: optionalText(10),
  city: optionalText(80),
  /** Locatie op de kaart (WGS84); lat en lng samen. */
  lat: z.number().min(-90).max(90).nullish(),
  lng: z.number().min(-180).max(180).nullish(),
  bagId: optionalText(40),
  clientName: optionalText(120),
  /** Klant (docs/CLIENTS.md); leeg bij aanmaken = "Nog in te delen". Wijzigen gaat via verplaatsen (K2). */
  clientId: z.uuid().nullish(),
  /** Locatie binnen de klant (optioneel). */
  siteId: z.uuid().nullish(),
  robawsClientId: optionalText(40),
  robawsProjectId: optionalText(40),
  /** Gebruiksfuncties (Dossier D2). */
  usageFunctions: usageFunctionsSchema.optional(),
});
const bothOrNeither = (b: { lat?: number | null; lng?: number | null }) => (b.lat == null) === (b.lng == null);
const latLngMsg = { message: "Locatie heeft lat én lng nodig", path: ["lat"] };
export const buildingCreateSchema = buildingFields.refine(bothOrNeither, latLngMsg);
export const buildingUpdateSchema = buildingFields
  .omit({ id: true, clientId: true, siteId: true })
  .partial()
  .refine((b) => (b.lat === undefined) === (b.lng === undefined) && bothOrNeither(b), latLngMsg);

/** Adres zoeken (PDOK Locatieserver, via de server). */
export const geoSuggestQuery = z.object({ q: z.string().trim().min(2).max(120) });
export const geoLookupQuery = z.object({ id: z.string().trim().min(1).max(80) });
export interface GeoSuggestion {
  id: string;
  label: string;
}
export interface GeoAddress {
  id: string;
  label: string;
  street: string | null;
  houseNumber: string | null;
  postcode: string | null;
  city: string | null;
  lat: number;
  lng: number;
}

export const floorCreateSchema = z.object({
  id: z.uuid().optional(),
  name: trimmed(80).min(1, "Vul een naam in"),
  level: trimmed(10).default(""),
  planFileId: z.uuid().nullish(),
  planPage: z.number().int().min(1).max(500).default(1),
});
export const floorUpdateSchema = z
  .object({
    name: trimmed(80).min(1),
    level: trimmed(10),
    planFileId: z.uuid().nullable(),
    planPage: z.number().int().min(1).max(500),
    /** Schaal van de tekening (docs/SPEC.md, Oppervlakte); vervalt bij een andere tekening of ander blad. */
    scale: floorScaleSchema.nullable(),
  })
  .partial();
export const floorReorderSchema = z.object({ ids: z.array(z.uuid()).min(1).max(500) });

export const roomCreateSchema = z.object({
  id: z.uuid().optional(),
  name: trimmed(80).min(1, "Vul een naam in"),
  shape: roomShapeSchema.nullish(),
});
export const roomUpdateSchema = z.object({ name: trimmed(80).min(1), shape: roomShapeSchema.nullable() }).partial();

/** Element volgens NLBE-SfB tabel 1: "56", "56.2" of "56.21" (docs/NLSFB.md). */
export const elementCodeSchema = z.string().trim().refine(isNlsfbCode, { message: "Onbekende elementcode" });

/** Asset (in de database historisch `installations`): installatie of bouwdeel met NLBE-SfB-element. */
const optText = (n: number) =>
  z.preprocess((v) => (typeof v === "string" && v.trim() === "" ? null : v), trimmed(n).nullish());
const coord01 = z.number().min(0).max(1);
const assetFields = z.object({
  name: trimmed(120).min(1, "Vul een naam in"),
  /** NLBE-SfB-element; null = niet ingedeeld. */
  elementCode: elementCodeSchema.nullish(),
  floorId: z.uuid().nullish(),
  roomId: z.uuid().nullish(),
  /** Plek op de tekening van `floorId` (0–1). */
  x: coord01.nullish(),
  y: coord01.nullish(),
  yearBuilt: z.number().int().min(1800).max(2100).nullish(),
  brand: optText(120),
  model: optText(120),
  serialNumber: optText(120),
  capacity: optText(120),
  warrantyUntil: z.iso.date().nullish(),
  notes: optText(2000),
  /** Installatie of bouwkundig deel (docs/CONDITIEMETING.md §5.5). */
  assetKind: z.enum(ASSET_KINDS).optional(),
  /** Assettype (docs/SYMBOLEN.md): bepaalt het symbool en vult een leeg element in. */
  assetType: z.enum(ASSET_TYPES).nullish(),
  /** Hoeveelheid en eenheid (nodig voor omvang en weging bij een conditiemeting). */
  quantity: z.number().min(0).max(1_000_000).nullish(),
  unit: z.enum(LIBRARY_UNITS).nullish(),
  /** Hoofdasset: dan is dit een onderdeel (één niveau, zelfde gebouw); null = zelfstandig. */
  parentAssetId: z.uuid().nullish(),
});
/** Filter op onderdelen in de assetlijst en de MJOP-planning. */
export const PART_FILTERS = ["all", "main", "parts"] as const;
export type PartFilter = (typeof PART_FILTERS)[number];
export const PART_FILTER_LABELS: Record<PartFilter, string> = {
  all: "Met onderdelen",
  main: "Zonder onderdelen",
  parts: "Alleen onderdelen",
};
export const assetCreateSchema = assetFields.extend({ id: z.uuid().optional() });
/** Zonder standaardwaarden: alleen meegestuurde velden wijzigen. */
export const assetUpdateSchema = assetFields.partial();
/** Dupliceren: één id per kopie (client-UUID's, idempotent). Maximaal 50 per keer. */
export const assetDuplicateSchema = z.object({ ids: z.array(z.uuid()).min(1).max(50) });
export type AssetDuplicateBody = z.input<typeof assetDuplicateSchema>;
/**
 * Bulkactie op een selectie in de assetlijst (assets, meters, arbeidsmiddelen): dezelfde wijziging voor
 * alle gekozen assets. Naam, serienummer, QR en plek op de tekening horen bij het exemplaar en zitten er
 * niet in. `onlyEmpty`: alleen velden aanvullen die bij een asset nog leeg zijn. `parentAssetId` koppelt
 * de selectie als onderdelen aan een hoofdasset (null maakt ze los).
 */
export const assetBulkSchema = z.object({
  ids: z.array(z.uuid()).min(1).max(500),
  set: assetFields
    .pick({
      assetKind: true,
      assetType: true,
      elementCode: true,
      brand: true,
      model: true,
      capacity: true,
      yearBuilt: true,
      warrantyUntil: true,
      quantity: true,
      unit: true,
      floorId: true,
      roomId: true,
      notes: true,
      parentAssetId: true,
    })
    .partial(),
  onlyEmpty: z.boolean().optional(),
});
export type AssetBulkBody = z.input<typeof assetBulkSchema>;
/** Bestaande QR-sticker koppelen: de gescande inhoud (URL of tekst). */
export const assetQrSchema = z.object({ qr: z.string().trim().min(1).max(500) });
export const assetFileBodySchema = z.object({ fileId: z.uuid(), kind: z.enum(ASSET_LINK_KINDS) });
export const qrGenerateSchema = z.object({ ids: z.array(z.uuid()).max(500).optional() });

export const PLAN_MIMES = ["application/pdf", "image/png", "image/jpeg", "image/webp"] as const;
export const PHOTO_MIMES = ["image/jpeg", "image/png", "image/webp"] as const;
/** Video's bij bevindingen. Opgeslagen als soort "photo" (zelfde koppeltabel), herkenbaar aan het MIME-type. */
export const VIDEO_MIMES = ["video/mp4", "video/quicktime", "video/webm"] as const;
export const isVideoMime = (mime: string | null | undefined) => !!mime && mime.startsWith("video/");
/** Video's worden niet verkleind op het apparaat, dus een ruimere grens dan foto's. */
export const MAX_VIDEO_BYTES = 250 * 1024 * 1024;
/** Bijlagen bij assets: documenten, tekeningen, handleidingen. */
export const ATTACHMENT_MIMES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
  "image/vnd.dwg",
  "application/acad",
  "application/x-dwg",
  "image/vnd.dxf",
] as const;
export const MAX_UPLOAD_BYTES: Record<(typeof FILE_KINDS)[number], number> = {
  plan: 50 * 1024 * 1024,
  photo: 15 * 1024 * 1024,
  report: 100 * 1024 * 1024,
  attachment: 25 * 1024 * 1024,
  document: 100 * 1024 * 1024,
};

export const presignBodySchema = z.object({
  id: z.uuid(),
  kind: z.enum(FILE_KINDS),
  mime: z.string().min(3).max(100),
  size: z.number().int().positive(),
  name: trimmed(200).min(1),
});
export const confirmBodySchema = z.object({
  pages: z.number().int().min(1).max(5000).optional(),
  width: z.number().positive().max(1e6).optional(),
  height: z.number().positive().max(1e6).optional(),
});

// ---------- antwoorden van de API ----------
export interface PlanFile {
  id: string;
  mime: string;
  name: string | null;
  pages: number | null;
  width: number | null;
  height: number | null;
}
export interface FloorDto {
  id: string;
  name: string;
  level: string;
  sort: number;
  planPage: number;
  plan: PlanFile | null;
  scale: FloorScale | null;
}
export interface RoomDto {
  id: string;
  floorId: string;
  name: string;
  shape: RoomShape | null;
}
export interface AssetDto {
  id: string;
  /** Leesbaar en uniek per organisatie: kenteken `B6Y-7CG`, een onderdeel `B6Y-7CG.1`. */
  code: string;
  name: string;
  /** NLBE-SfB-element ("56.24"), of null. */
  elementCode: string | null;
  floorId: string | null;
  roomId: string | null;
  x: number | null;
  y: number | null;
  yearBuilt: number | null;
  brand: string | null;
  model: string | null;
  serialNumber: string | null;
  capacity: string | null;
  warrantyUntil: string | null;
  notes: string | null;
  /** Inhoud/token van de gekoppelde QR-code, of null. */
  qrCode: string | null;
  /** Herkomst bij een import ("assetlog") en de code daar, of null. */
  externalSource: string | null;
  externalId: string | null;
  assetKind: AssetKind;
  /** Assettype (symbool), of null: dan het symbool van het element. */
  assetType: AssetType | null;
  /** Verwachte levensduur (jaar), voor de theoretische conditie (docs/CONDITIEMETING.md §7a). */
  lifespanYears: number | null;
  quantity: number | null;
  unit: LibraryUnit | null;
  /** Laatste afgeronde conditiemeting (cache); null = nog niet gemeten. */
  conditionScore: number | null;
  conditionAssessedOn: string | null;
  /** Aantal bijlagen (zonder foto's). */
  fileCount: number;
  /** Hoofdfoto (de eerste foto) om de asset te herkennen, of null. */
  photoId: string | null;
  photoCount: number;
  /** Slechtste status van de keuringen; null zonder verplichtingen (docs/ASSETS-MJOP.md). */
  obligationStatus: ObligationStatus | null;
  /** Hoofdasset als dit een onderdeel is (code `B6Y-7CG.1`), anders null. */
  parentAssetId: string | null;
}
export interface AssetFileDto {
  fileId: string;
  kind: AssetLinkKind;
  name: string;
  mime: string;
  size: number | null;
  createdAt: string;
}
/** Onderdeel in het tabblad *Onderdelen* van de hoofdasset. */
export interface AssetPartDto {
  id: string;
  code: string;
  name: string;
  brand: string | null;
  model: string | null;
  floorId: string | null;
  roomId: string | null;
  x: number | null;
  y: number | null;
  /** Aantal open bevindingen aan dit onderdeel. */
  openIssues: number;
}
export interface AssetDetail extends AssetDto {
  buildingId: string;
  /** Foto's en (voor oudere clients) de documenten die direct aan de asset hangen. */
  files: AssetFileDto[];
  /** Documenten uit het dossier: direct gekoppeld of via de hoofdasset (`via`). */
  documents: AssetDocumentDto[];
  /** Bevindingen aan de asset en (bij een hoofdasset) aan de onderdelen; `asset` is dan het onderdeel. */
  issues: {
    id: string;
    code: string;
    title: string;
    status: string;
    priority: string;
    asset: { id: string; code: string; name: string } | null;
  }[];
  /** Hoofdasset van dit onderdeel, of null. */
  parent: { id: string; code: string; name: string } | null;
  /** Onderdelen van deze hoofdasset (op code). */
  parts: AssetPartDto[];
}
export interface BuildingSummary {
  id: string;
  code: string;
  name: string;
  address: string | null;
  city: string | null;
  lat: number | null;
  lng: number | null;
  clientId: string;
  siteId: string | null;
  /** Voorbeeldgebouw uit Aan de slag (issue #162). */
  isDemo: boolean;
  /** Actieve bevindingen en daarvan P1/P2 (voor de kaart en de lijst). */
  openCount?: number;
  urgentCount?: number;
}
export interface ArchivedBuilding {
  id: string;
  code: string;
  name: string;
  address: string | null;
  archivedAt: string;
}
/** Bevestiging bij definitief verwijderen: de gebouwcode intypen. */
export const buildingDeleteSchema = z.object({ code: z.string().trim().min(1) });
export interface BuildingDto extends BuildingSummary {
  street: string | null;
  houseNumber: string | null;
  postcode: string | null;
  bagId: string | null;
  clientName: string | null;
  /** Klant en locatie (docs/CLIENTS.md). */
  client: { id: string; name: string; isUnassigned: boolean };
  site: { id: string; name: string } | null;
  robawsClientId: string | null;
  robawsProjectId: string | null;
  /** Code ligt vast zodra er bevindingen zijn. */
  codeLocked: boolean;
  /** Aantal bevindingen (ook verwijderde), o.a. voor de waarschuwing bij definitief verwijderen. */
  issueCount: number;
  /** Melden zonder inloggen via de QR-code van een asset (docs/MELDINGEN.md §2). */
  intakeAssetQr: boolean;
  /** Gebruiksfuncties (Dossier D2). */
  usageFunctions: UsageFunction[];
  floors: FloorDto[];
  rooms: RoomDto[];
  assets: AssetDto[];
}
export interface PresignResponse {
  fileId: string;
  uploadUrl: string;
  headers: Record<string, string>;
}
